/**
 * @fileoverview Tenant Users API service
 * @module services/usersApi
 * @description API calls for tenant user management
 */

import { supabase } from '../lib/supabase';
import { apiFetch } from '../lib/api';
import type {
  TenantUser,
  TenantUserStats,
  InviteTenantUserInput,
  UpdateTenantUserInput,
  TenantInvite,
} from '../types/users';

/**
 * Get auth headers with current session token
 */
async function getAuthHeaders(): Promise<HeadersInit> {
  const { data: { session } } = await supabase.auth.getSession();
  return {
    'Content-Type': 'application/json',
    ...(session?.access_token && { Authorization: `Bearer ${session.access_token}` }),
  };
}

/**
 * Tenant Users API service
 */
export const usersApi = {
  /**
   * Get all members of a tenant
   */
  async getUsers(tenantId: string): Promise<TenantUser[]> {
    const headers = await getAuthHeaders();
    const response = await apiFetch(`/api/tenants/${tenantId}/members`, { headers });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Failed to fetch users');
    }

    return response.json();
  },

  /**
   * Get member statistics for a tenant
   */
  async getUserStats(tenantId: string): Promise<TenantUserStats> {
    const headers = await getAuthHeaders();
    const response = await apiFetch(`/api/tenants/${tenantId}/members/stats`, { headers });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Failed to fetch user stats');
    }

    return response.json();
  },

  /**
   * Invite a new user to the tenant
   */
  async inviteUser(tenantId: string, data: InviteTenantUserInput): Promise<{ message: string; inviteId: string }> {
    const headers = await getAuthHeaders();
    const response = await apiFetch(`/api/tenants/${tenantId}/members/invite`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Failed to invite user');
    }

    return response.json();
  },

  /**
   * Update a tenant member's role
   */
  async updateUser(tenantId: string, userId: string, data: UpdateTenantUserInput): Promise<TenantUser> {
    const headers = await getAuthHeaders();
    const response = await apiFetch(`/api/tenants/${tenantId}/members/${userId}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Failed to update user');
    }

    return response.json();
  },

  /**
   * Remove a user from the tenant
   */
  async removeUser(tenantId: string, userId: string): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await apiFetch(`/api/tenants/${tenantId}/members/${userId}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Failed to remove user');
    }
  },

  /**
   * Deactivate a user (set isActive to false)
   */
  async deactivateUser(tenantId: string, userId: string): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await apiFetch(`/api/tenants/${tenantId}/members/${userId}/deactivate`, {
      method: 'POST',
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Failed to deactivate user');
    }
  },

  /**
   * Reactivate a user (set isActive to true)
   */
  async reactivateUser(tenantId: string, userId: string): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await apiFetch(`/api/tenants/${tenantId}/members/${userId}/reactivate`, {
      method: 'POST',
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || 'Failed to reactivate user');
    }
  },
};

export default usersApi;
